import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Layout from "./components/common/layout/Layout";
import PublicLayout from "./components/common/layout/PublicLayout";
import { HeroUIProvider, ToastProvider } from "@heroui/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BottomScrollProvider } from "./hooks/useBottomScrollContext";
import { FeatureFlagProvider } from "./context/FeatureFlagProvider";
import { UseCasesProvider } from "./context/UseCasesProvider";
import { WebSocketProvider } from "./context/WebSocketContext";
import { NavigationBlockerProvider } from "./context/NavigationBlockerContext";
import useAuth from "./hooks/useAuth";
import { getCurrentUser, fetchAuthSession } from "aws-amplify/auth";

import LoginPage from "./pages/LoginPage";
import SupportPage from "./pages/SupportPage";
import OrdersPage from "./pages/OrdersPage";
import OrderPage from "./pages/OrderPage";
import DashboardPage from "./pages/DashboardPage";
import UsersPage from "./pages/UsersPage";
import UserPage from "./pages/UserPage";
import BillingPage from "./pages/BillingPage";
import CustomersPage from "./pages/CustomersPage";
import ErrorOrderPage from "./pages/ErrorOrderPage";
import DebugOrderPage from "./pages/DebugOrderPage";
import UnAuthorizedPage from "./pages/UnAuthorizedPage";
import CustomerSettingsPage from "./pages/CustomerSettingsPage";
import CustomerGroupSettingsPage from "./pages/CustomerGroupSettingsPage";
import SettingsPage from "./pages/SettingsPage";
import DatabasePage from "./pages/DatabasePage";

import RequireAuth from "./components/auth/RequireAuth";
import RequireProfile from "./components/auth/RequireProfile";

const queryClient = new QueryClient();

function App() {
  const { auth, setAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchAuthData = async () => {
      try {
        const user = await getCurrentUser();
        const authSession = (await fetchAuthSession()).tokens;
        const accessToken = authSession?.accessToken?.toString() || null;
        const clientId = authSession?.idToken?.payload?.profile
          ? String(authSession.idToken.payload.profile)
          : null;

        setAuth({ accessToken, userFromCognito: user, clientId });
      } catch (error) {
        console.log("No authenticated user:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAuthData();
  }, [setAuth]);

  if (loading || !auth) {
    return <div>Loading...</div>;
  }

  return (
    <HeroUIProvider>
      <ToastProvider
        toastProps={{
          variant: "solid",
          timeout: 4000,
          hideIcon: true,
          classNames: { closeButton: "opacity-100 absolute right-4 top-1/2 -translate-y-1/2" },
        }}
      />
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        {auth?.userFromCognito?.userId ? (
          <FeatureFlagProvider clientId={auth.clientId ?? undefined}>
            <UseCasesProvider>
              <WebSocketProvider>
                <NavigationBlockerProvider>
                  <BottomScrollProvider>
                    <Routes>
                    {/* {public routes} */}
                    <Route element={<PublicLayout />}>
                      <Route path="login" element={<LoginPage />} />
                      <Route path="support" element={<SupportPage />} />
                      <Route path="unauthorized" element={<UnAuthorizedPage />} />
                    </Route>

                    {/* {restricted routes} */}
                    <Route element={<Layout />}>
                      <Route element={<RequireAuth />}>
                        <Route path="orders" element={<OrdersPage />} />
                        <Route path="orders/:orderId" element={<OrderPage />} />
                        <Route path="orderserror/:orderId" element={<ErrorOrderPage />} />
                        <Route path="database" element={<DatabasePage />} />
                        <Route path="debug/:orderId/:indexId?" element={<DebugOrderPage />} />
                      </Route>

                      {/* Routes supervisorOnly */}
                      <Route element={<RequireProfile supervisorOnly />}>
                        <Route path="dashboard" element={<DashboardPage />} />
                        <Route path="users" element={<UsersPage />} />
                        <Route path="users/:userId" element={<UserPage />} />
                      </Route>

                      {/* Routes adminOnly */}
                      <Route element={<RequireProfile adminOnly />}>
                        <Route path="customers" element={<CustomersPage />} />
                        <Route
                          path="customers/settings/:customerId"
                          element={<CustomerSettingsPage />}
                        />
                        <Route
                          path="customersgroup/settings/:customerGroupId"
                          element={<CustomerGroupSettingsPage />}
                        />
                      </Route>

                      {/* Routes adminVistaFlowOnly */}
                      <Route element={<RequireProfile adminVistaFlowOnly />}>
                        <Route path="billing" element={<BillingPage />} />
                        <Route path="settings" element={<SettingsPage />} />
                      </Route>
                    </Route>

                    <Route path="*" element={<LoginPage />} />
                  </Routes>
                  </BottomScrollProvider>
                </NavigationBlockerProvider>
              </WebSocketProvider>
            </UseCasesProvider>
          </FeatureFlagProvider>
        ) : (
          <BottomScrollProvider>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="login" element={<LoginPage />} />
                <Route path="support" element={<SupportPage />} />
                <Route path="unauthorized" element={<UnAuthorizedPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/login" />} />
              <Route path="/" element={<Navigate to="/login" />} />
            </Routes>
          </BottomScrollProvider>
        )}
      </QueryClientProvider>
    </HeroUIProvider>
  );
}

export default App;
